import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'threads5/styles/styled';
import type {
  ChangelogProduct,
  ChangelogProductCategory,
  ChangelogProductId,
} from 'src/components/docs/Changelog/products';
import {
  CATEGORY_ORDER,
  GENERAL_PRODUCT_ID,
  PRODUCTS,
} from 'src/components/docs/Changelog/products';
import { useChangelogFilter } from 'src/components/docs/Changelog/ChangelogFilterContext';
import FoundationIcon from 'src/components-v2/DataDisplay/Icon/FoundationIcon';

const StyledFilter = styled('div')(({ theme }) => {
  return {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginBottom: '24px',
    [theme.breakpoints.up('md')]: {
      marginBottom: '32px',
    },
  };
});

const StyledTrigger = styled('button', {
  shouldForwardProp: (prop) => {
    return prop !== 'isOpen';
  },
})<{ isOpen: boolean }>(({ theme, isOpen }) => {
  return {
    fontFamily: "'Plaid Sans', Helvetica, Arial, sans-serif",
    fontSize: '1.4rem',
    lineHeight: '2rem',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    minWidth: '240px',
    padding: '8px 14px',
    borderRadius: '4px',
    border: `1px solid ${isOpen ? '#111' : '#c5c6cc'}`,
    background: '#fff',
    color: '#111',
    transition: 'border-color 120ms ease',
    '&:hover': {
      borderColor: '#111',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette['base/sky/500'] ?? '#0070f3'}`,
      outlineOffset: '2px',
    },
  };
});

const StyledChevron = styled('span', {
  shouldForwardProp: (prop) => {
    return prop !== 'isOpen';
  },
})<{ isOpen: boolean }>(({ isOpen }) => {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    transition: 'transform 120ms ease',
    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
  };
});

const StyledPanel = styled('div')(({ theme }) => {
  return {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    zIndex: 10,
    width: '280px',
    maxHeight: '360px',
    display: 'flex',
    flexDirection: 'column',
    background: '#fff',
    border: '1px solid #c5c6cc',
    borderRadius: '4px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    overflow: 'hidden',
    [theme.breakpoints.up('md')]: {
      width: '320px',
    },
  };
});

const StyledOptionList = styled('div')({
  flex: 1,
  overflowY: 'auto',
  padding: '4px 0',
});

const StyledCategoryHeader = styled('div')({
  fontFamily: "'Plaid Sans', Helvetica, Arial, sans-serif",
  fontSize: '1.1rem',
  lineHeight: '1.6rem',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#666',
  padding: '10px 14px 4px',
});

const StyledOption = styled('label')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '6px 14px',
  cursor: 'pointer',
  fontFamily: "'Plaid Sans', Helvetica, Arial, sans-serif",
  fontSize: '1.4rem',
  lineHeight: '2rem',
  color: '#111',
  '&:hover': {
    background: '#f5f5f7',
  },
});

const StyledCheckbox = styled('input')({
  width: '16px',
  height: '16px',
  flexShrink: 0,
  cursor: 'pointer',
  accentColor: '#111',
  margin: '0 !important',
});

const StyledFooter = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px 14px',
  borderTop: '1px solid #e5e5e7',
  background: '#fafafa',
});

const StyledFooterButton = styled('button', {
  shouldForwardProp: (prop) => {
    return prop !== 'variant';
  },
})<{ variant: 'primary' | 'tertiary' }>(({ theme, variant }) => {
  return {
    fontFamily: "'Plaid Sans', Helvetica, Arial, sans-serif",
    fontSize: '1.3rem',
    lineHeight: '1.8rem',
    cursor: 'pointer',
    padding: variant === 'primary' ? '6px 14px' : '6px 0',
    borderRadius: '4px',
    border: variant === 'primary' ? '1px solid #111' : 'none',
    background: variant === 'primary' ? '#111' : 'transparent',
    color: variant === 'primary' ? '#fff' : '#444',
    '&:enabled:hover': {
      textDecoration: variant === 'tertiary' ? 'underline' : 'none',
      background: variant === 'primary' ? '#000' : 'transparent',
    },
    '&:disabled': {
      cursor: 'default',
      opacity: 0.5,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette['base/sky/500'] ?? '#0070f3'}`,
      outlineOffset: '2px',
    },
  };
});

const StyledStatus = styled('div')({
  fontFamily: "'Plaid Sans', Helvetica, Arial, sans-serif",
  fontSize: '1.3rem',
  lineHeight: '1.8rem',
  color: '#666',
});

const ChangelogFilter: FC = () => {
  const { activeProducts, setActiveProducts, isHydrated } =
    useChangelogFilter();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const closeAndRestoreFocus = useCallback((): void => {
    setIsOpen(false);
    triggerRef.current?.focus();
  }, []);

  const toggleProduct = useCallback(
    (id: ChangelogProductId): void => {
      const next = new Set(activeProducts);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      // First time the user picks any non-general product, auto-add `general`
      // so cross-product announcements are visible by default. They can still
      // uncheck it explicitly for a strict per-product view.
      if (
        activeProducts.size === 0 &&
        next.size > 0 &&
        id !== GENERAL_PRODUCT_ID
      ) {
        next.add(GENERAL_PRODUCT_ID);
      }
      setActiveProducts(next);
    },
    [activeProducts, setActiveProducts],
  );

  const clearAll = useCallback((): void => {
    setActiveProducts(new Set());
  }, [setActiveProducts]);

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: PointerEvent): void => {
      if (
        containerRef.current != null &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const count = activeProducts.size;
  const triggerLabel = useMemo<string>(() => {
    if (count === 0) return 'Filter by product';
    return `Filter by product (${count})`;
  }, [count]);

  const statusText = useMemo<string>(() => {
    if (count === 0) return 'Showing all entries';
    const labels = PRODUCTS.filter((p) => {
      return activeProducts.has(p.id);
    }).map((p) => {
      return p.label;
    });
    return `Showing entries for: ${labels.join(', ')}`;
  }, [activeProducts, count]);

  return (
    <StyledFilter
      ref={containerRef}
      style={{ visibility: isHydrated ? 'visible' : 'hidden' }}
    >
      <StyledTrigger
        ref={triggerRef}
        type='button'
        isOpen={isOpen}
        aria-haspopup='true'
        aria-expanded={isOpen}
        onClick={() => {
          setIsOpen((prev) => {
            return !prev;
          });
        }}
      >
        <span>{triggerLabel}</span>
        <StyledChevron isOpen={isOpen} aria-hidden='true'>
          <FoundationIcon icon='ChevronDown' size={14} omitWrapper />
        </StyledChevron>
      </StyledTrigger>

      {isOpen && (
        <StyledPanel aria-label='Product filters'>
          <StyledOptionList>
            {CATEGORY_ORDER.map((category) => {
              const productsInCategory = PRODUCTS.filter((p) => {
                return p.category === category;
              });
              if (productsInCategory.length === 0) return null;
              return (
                <div key={category}>
                  <StyledCategoryHeader>{category}</StyledCategoryHeader>
                  {productsInCategory.map((product) => {
                    const isChecked = activeProducts.has(product.id);
                    return (
                      <StyledOption key={product.id}>
                        <StyledCheckbox
                          type='checkbox'
                          checked={isChecked}
                          onChange={() => {
                            toggleProduct(product.id);
                          }}
                        />
                        {product.label}
                      </StyledOption>
                    );
                  })}
                </div>
              );
            })}
          </StyledOptionList>
          <StyledFooter>
            <StyledFooterButton
              type='button'
              variant='tertiary'
              onClick={clearAll}
              disabled={count === 0}
            >
              Clear all
            </StyledFooterButton>
            <StyledFooterButton
              type='button'
              variant='primary'
              onClick={closeAndRestoreFocus}
            >
              Done
            </StyledFooterButton>
          </StyledFooter>
        </StyledPanel>
      )}

      <StyledStatus role='status' aria-live='polite'>
        {statusText}
      </StyledStatus>
    </StyledFilter>
  );
};

export default ChangelogFilter;
